/* eslint-disable no-underscore-dangle */
import { Box, IBoxProps, IPressableProps, IStackProps, ITextProps, Pressable } from 'native-base';
import React, { ReactNode, useMemo, useState } from 'react';
import Collapsible, { CollapsibleProps } from 'react-native-collapsible';

import ArrowDown from 'app/components/icons/ArrowDown';
import IconTitle, { IconTitleProps, VERTICAL_PADDING } from 'app/components/IconTitle';
import Rotate from 'app/components/Rotate';
import { tID } from 'app/services/TestHelper';

type AccordionSection = {
  id: string;
  title: ReactNode;
  subtitle?: ReactNode;
  status?: ReactNode;
  icon?: JSX.Element;
  content: ReactNode;
  initialOpen?: boolean;
  _collapsible?: CollapsibleProps;
  _container?: IBoxProps;
  _trigger?: IPressableProps;
  _status?: IStackProps;
  _title?: IconTitleProps['_title'];
  _subtitle?: ITextProps;
  onToggle?: (isOpen: boolean) => void;
  open?: boolean;
};

type AccordionSectionProps = {
  section: AccordionSection;
  index: number;
  onExpand?: () => void;
};

function AccordionSection({ section, index, onExpand }: AccordionSectionProps) {
  const { onToggle } = section;
  const controlledOpen = section.open;
  const [open, setOpen] = useState(section.initialOpen ?? false);

  useMemo(() => {
    if (controlledOpen !== undefined && controlledOpen !== open) {
      setOpen(controlledOpen);
    }
  }, [controlledOpen, open]);

  const handleToggle = () => {
    if (onToggle) onToggle(!open);
    if (!open && onExpand) onExpand();
    setOpen(!open);
  };

  return (
    <Box
      key={section.id}
      borderBottomWidth="1"
      borderColor="gray.300"
      borderTopWidth={index === 0 ? '1px' : 0}
      pb={open ? '4' : '0'}
      w="full"
      {...section._container}
    >
      <Pressable
        py={VERTICAL_PADDING}
        onPress={handleToggle}
        {...section._trigger}
        _pressed={{ bg: 'muted.100' }}
        testID={tID(`Accordion-${section.id}`)}
        w="full"
      >
        <IconTitle
          _hstack={section._status}
          _subtitle={section._subtitle}
          _title={section._title}
          icon={section.icon}
          statusNode={section.status}
          subtitle={section.subtitle}
          title={section.title}
        >
          <Box>
            <Rotate end={180} rotated={open} start={0}>
              <ArrowDown size={16} />
            </Rotate>
          </Box>
        </IconTitle>
      </Pressable>

      <Collapsible collapsed={!open} renderChildrenCollapsed {...section._collapsible}>
        <Box>{section.content}</Box>
      </Collapsible>
    </Box>
  );
}

export type AccordionProps<T extends AccordionSection> = IBoxProps & {
  sections: T[];
  onExpand?: (data: T, idx: number) => void;
};

export default function Accordion<T extends AccordionSection>({
  sections = [],
  onExpand,
  ...props
}: AccordionProps<T>) {
  return (
    <Box testID={tID('Accordion')} {...props}>
      {sections.map((section, idx) => (
        <AccordionSection
          key={section.id}
          index={idx}
          section={section}
          onExpand={onExpand ? () => onExpand(section, idx) : undefined}
        />
      ))}
    </Box>
  );
}
